import { IVfxTask } from "../../core/runtime/vfx_task_runner.js";
import { Vfx2DRuntime } from "../../vfx_2d_runtime.js";
import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { GenericSpriteBatch } from "../../core/batching/generic_sprite_batch.js";
import { IController, StopBehavior } from "../../core/types/controller_types.js";
import { remap } from "../../../common/visual_fx_common_funcs.js";
import { ColorMode } from "../../core/types/webgl_types.js";

export class VfxCoinCounterGlowController implements IVfxTask, IController
{
  private static readonly ROTATION_SPEED   : number = 1.2;
  private static readonly SCALE_FACTOR     : number = 3;
  private static readonly MAX_ALPHA        : number = 0.5;

  private target: Vector2;
  private start_time: number;
  private duration: number;
  private glow_size: number;
  private glow_ids: number[] = [];
  private is_finished: boolean = false;
  private render_on_back: boolean;
  private scale_in_duration: number;
  private rotation_speed: number;

  constructor(
    target: Vector2,
    target_size: number,
    start_time: number,
    duration: number = 500,
    render_on_back: boolean = false,
    scale_in_duration: number = 0,
    rotation_speed: number = VfxCoinCounterGlowController.ROTATION_SPEED
  )
  {
    this.target = target.clone();
    this.start_time = start_time;
    this.duration = duration;
    this.render_on_back = render_on_back;
    this.scale_in_duration = scale_in_duration;
    this.rotation_speed = rotation_speed;
    this.glow_size = target_size * VfxCoinCounterGlowController.SCALE_FACTOR;
    const sprite_lifetime = duration > 0 ? duration : Number.MAX_SAFE_INTEGER;

    const sprite_batch = this.spriteBatch();

    // additive=0 (not 1): additive=1 makes the shader write alpha=0, which Safari's
    // canvas-to-page compositor renders as fully transparent on this alpha:true
    // WebGL canvas — invisible on Mac even though Chromium shows it fine.
    const id1 = sprite_batch.spawnSprite(
      ImgID.RaysGlow1,
      this.target,
      new Vector2( this.glow_size, this.glow_size ),
      { x: 1.0, y: 0.8, z: 0.3 },
      0.0,
      0.0,
      0.0,
      this.start_time,
      sprite_lifetime,
      ColorMode.Mask,
    );

    const id2 = sprite_batch.spawnSprite(
      ImgID.RaysGlow1,
      this.target,
      new Vector2( this.glow_size, this.glow_size ),
      { x: 1.0, y: 0.8, z: 0.3 },
      0.0,
      0.0,
      0.0,
      this.start_time,
      sprite_lifetime,
      ColorMode.Mask,
    );

    if ( id1 !== -1 ) this.glow_ids.push( id1 );
    if ( id2 !== -1 ) this.glow_ids.push( id2 );
  }

  public setPosition( pos: Vector2 ): void
  {
    this.target.copyFrom( pos );
  }

  public accelerate( factor: number, now: number ): void {}

  public stop( behavior?: StopBehavior, fade_duration?: number ): void
  {
    const sprite_batch = this.spriteBatch();
    const now = performance.now();
    for ( const id of this.glow_ids )
    {
      sprite_batch.triggerDeath( id, now );
    }
    this.glow_ids.length = 0;
    this.is_finished = true;
  }

  public onUpdate( now: number ): boolean
  {
    if ( this.is_finished ) return true;

    const elapsed = now - this.start_time;
    if ( elapsed < 0 ) return false;

    if ( this.duration > 0 && elapsed >= this.duration )
    {
      this.stop();
      return true;
    }

    const sprite_batch = this.spriteBatch();
    const elapsed_sec = elapsed * 0.001;
    const progress = this.duration > 0 ? elapsed / this.duration : 0;
    const reveal_progress = this.scale_in_duration > 0
      ? Math.min( 1, elapsed / this.scale_in_duration )
      : 1;
    const reveal_eased = 1 - ( 1 - reveal_progress ) * ( 1 - reveal_progress );
    const reveal_scale = 0.45 + 0.55 * reveal_eased;
    const current_glow_size = this.glow_size * reveal_scale;

    const alpha = VfxCoinCounterGlowController.MAX_ALPHA * remap( progress, 0.6, 1.0, 1.0, 0.0, true );

    const angle1 = elapsed_sec * this.rotation_speed;
    const angle2 = -elapsed_sec * this.rotation_speed;

    if ( this.glow_ids[0] != null )
    {
      sprite_batch.updateSpriteData(
        this.glow_ids[0],
        this.target,
        new Vector2( current_glow_size, current_glow_size ),
        alpha,
        angle1
      );
    }
    if ( this.glow_ids[1] != null )
    {
      sprite_batch.updateSpriteData(
        this.glow_ids[1],
        this.target,
        new Vector2( current_glow_size, current_glow_size ),
        alpha,
        angle2
      );
    }

    return false;
  }

  private spriteBatch(): GenericSpriteBatch
  {
    return this.render_on_back
      ? Vfx2DRuntime.onOverlayBack( GenericSpriteBatch )
      : Vfx2DRuntime.onOverlayFront( GenericSpriteBatch );
  }
}
